// ============================================================
// Client/src/components/Dashboard/SLATEChatbot.jsx
// SLATE AI Help Assistant — Role-first, AI-powered, Robot Icon
// ============================================================

import { useState, useEffect, useRef } from 'react';

/* ══════════════════════════════════════════════════════════
   KNOWLEDGE BASE  (roles: 1=Admin  2=Mentor  3=StateOfficer)
══════════════════════════════════════════════════════════ */
const KNOWLEDGE = {

  'add school': {
    title: 'How to Add a School',
    roles: [1, 2, 3],
    steps: [
      'Go to School section in the left sidebar.',
      'Click "Add School" from the dropdown menu.',
      'Fill School Name (max 150 chars) and UDISE Code (must be unique).',
      'Select State and District from the dropdowns.',
      'Enter Pincode and Address.',
      'Optionally fill: Geo Location, School Email ID, Primary Contact Person, Contact No, Total Students.',
      'Click Submit to save the school.',
    ],
    note: 'UDISE code must be unique — duplicate entries will be rejected. All three roles (Admin, Mentor, State Officer) can add schools.',
  },

  'tag lab to school': {
    title: 'How to Tag a Lab to a School',
    roles: [1, 2, 3],
    steps: [
      'Go to School in the sidebar.',
      'Click "Tag Lab to School".',
      'Enter Lab Name (required).',
      'Select Lab Type: Atal Tinkering Lab, Aghaaz Lab, SATHEE, or Artificial Intelligence Lab.',
      'Enter Lab Capacity (number of students) and Lab Description (max 500 chars).',
      'Select the School Name from the dropdown.',
      'Click Submit to link the lab to the school.',
    ],
    note: 'A lab must be tagged to a school before sessions can be created. All three roles can tag labs.',
  },

  'add equipment': {
    title: 'How to Add Equipment',
    roles: [1, 2, 3],
    steps: [
      'Go to School in the sidebar.',
      'Click "Add Equipment".',
      'Enter Equipment Name (max 100 chars) and Equipment Description (max 250 chars).',
      'Select Warranty Status from the dropdown.',
      'Enter Quantity, Date of Expiry, and Serial Number.',
      'Click Submit to save the equipment.',
    ],
    note: 'After adding equipment, use Equipment Allocation to assign it to a school or lab. All three roles can add equipment.',
  },

  'equipment allocation': {
    title: 'How to Allocate Equipment',
    roles: [1, 2, 3],
    steps: [
      'Go to School in the sidebar.',
      'Click "Equipment Allocation".',
      'Select one or more School Names from the multi-select list.',
      'Select the Lab Name from the dropdown.',
      'Select one or more Equipment Names from the multi-select list (shows available quantity).',
      'Set the Allocation Date.',
      'The Available Quantity field auto-fills. Enter the Allocation Quantity.',
      'Click Submit to complete the allocation.',
    ],
    note: 'Equipment must be added first before it can be allocated. All three roles can allocate equipment.',
  },

  'session': {
    title: 'How to Create a Session',
    roles: [1, 2, 3],
    steps: [
      'Go to School in the sidebar.',
      'Click "Session Setup".',
      '— Section 1: Enter Session Title, select Session Host, Session Date, Session Time (with AM/PM).',
      'Select School Name and Lab Name from dropdowns.',
      'Enter Session Description (max 300 chars).',
      '— Section 2 (SATHEE sessions): Fill Centre Code, State, District/Block/Zone.',
      'Enter Name of Coordinator, select Type of School Visited (Govt/Private/Aided/Other).',
      'Enter School Address, Principal Name, and Principal/POC Contact.',
      'Select Mode of Visit: In-person, Virtual, or Phone Call.',
      'Upload List of Invitees Excel file (.xls/.xlsx) — Download Template link is available.',
      'Upload Session Images (optional, max 10 images — appear in PDF reports).',
      'Click Submit at the bottom.',
    ],
    note: 'The Invitees Excel file is required. Lab must be tagged to a school before a session can be created.',
  },

  'my entries': {
    title: 'How to Use My Entries — All Entries',
    roles: [1, 2, 3],
    steps: [
      'Go to School in the sidebar.',
      'Click "My Entries - All Entries".',
      '— TOGGLE: "My Entities" shows only your own data. "All Entities" shows all data (Admin sees all; Mentor and State Officer see their own).',
      '',
      '📂 SCHOOLS TAB',
      '  • Table shows: School Name, School State, Added By, Added On, Status, Action (Edit).',
      '  • Admin (All Entities): sees all 184 schools across all states.',
      '  • Mentor/State Officer (My Entities): sees only schools they added.',
      '',
      '📂 LABS TAB',
      '  • Table shows: Lab Name, School, Added By, Added On, Status, Action (Edit).',
      '  • Admin sees all labs. Mentor sees only their own labs.',
      '',
      '📂 EQUIPMENT TAB',
      '  • Table shows: Equipment Name, Description, Warranty, Quantity, Serial No, Added On.',
      '',
      '📂 ALLOCATED EQUIPMENT TAB',
      '  • Table shows: Equipment Name, School Name, School State, Lab Name, Allocated Quantity, Allocated By, Allocated On, Action (Edit).',
      '',
      '📂 SESSIONS TAB',
      '  • Table shows: Session Title, Session Host, Session Date, Setup By, Images, Download, Action (Track).',
      '  • Images column: shows a thumbnail of the first session image. If multiple images, shows "+N more".',
      '  • Download button: downloads ALL session images as a ZIP file (e.g. "Testing Final_images.zip").',
      '  • After downloading: extract the ZIP folder to view image_1, image_2, etc. as PNG files.',
      '  • Admin: can download images for ALL sessions (All Entities toggle).',
      '  • Mentor: can only download images for their OWN sessions (My Entities view only).',
      '  • State Officer: CANNOT download session images — no Download button visible.',
      '  • Track button: opens session tracking/attendance details.',
      '',
      '📂 SATHEE STUDENTS TAB',
      '  • Table shows: ID, Student Name, School, Mobile Number, Email, Registration Date, Action (Delete).',
      '  • Admin sees all 795+ registered students.',
      '  • Can search and paginate through all student records.',
    ],
    note: 'Session images download as a ZIP file named "SessionTitle_images.zip". Extract it to see individual PNG images. State Officers cannot download session images. Mentors can only download images from their own sessions. Admins can download from any session.',
  },

  'sathee registration': {
    title: 'How to Register a SATHEE Student',
    roles: [1, 2, 3],
    steps: [
      'Go to School in the sidebar.',
      'Click "Sathee Registration".',
      '— Personal Information: Enter Name, Father Name, Date of Birth, Email ID.',
      'Select Gender, Social Group from dropdowns. Enter Mobile Number (optional/WhatsApp).',
      '— Location: Select Country (India default), State, District, and enter City/Village.',
      '— School & Course: Select Organization Type, School Type, Class.',
      'Enter School Name and Course Opted (optional, e.g. AI/ML, Robotics, 3D Printing).',
      '— How Did You Know About SATHEE: Check applicable options (Advertisement, School, Friends, Internet, College, Library, Social Media, Other).',
      'Accept Terms and Conditions checkbox.',
      'Click Submit to register. Use Clear to reset the form.',
    ],
    note: 'After submission the student appears in the Registered Students table below the form (currently 781 students). SATHEE registrations are tracked separately from session attendance.',
  },

  'library': {
    title: 'How to Use the Library',
    roles: [1, 2, 3],
    steps: [
      'Click "Library" in the left sidebar.',
      '— Search: Search artifacts by typing keywords or selecting tags.',
      '— My Artifacts: Shows only documents you have uploaded.',
      '— Upload Document: Click Upload Document, select your file, choose Document Type, add Tags, write a Description, toggle Publish, and click Submit.',
      '— Add URL: Click Add URL, enter Title and URL, add Tags, select Document Type, write Description, toggle Publish, and click Submit.',
      '— Manage All Artifacts (Admin only): View and edit/delete any document uploaded by any user.',
    ],
    note: 'File size must be within the upload limit set in System Settings (currently 2500 KB). Allowed formats are configured by the Administrator (jpg, png, pdf, docx, xlsx, etc.).',
  },

  'reports': {
    title: 'Reports & Analytics — Overview',
    roles: [1, 2, 3],
    steps: [
      'Click "Reports" in the sidebar.',
      'The page has 3 tabs: Dashboard Stats, Download Reports (Excel), and PDF Reports.',
      '— Admin: Full Access · 26 reports available · can filter by State.',
      '— State Officer: Your State Only · 22 reports · state filter is locked to your state.',
      '— Mentor: Your Lab Only · 11 reports · marked "Restricted to your assigned lab".',
      'Dashboard Stats: Summary cards + Sessions per Month bar chart.',
      'Download Reports: Choose Excel report type → set date filters → click Download Excel Report.',
      'PDF Reports: Choose PDF type → set filters → click Download PDF.',
    ],
    note: 'Admin sees all states and labs. State Officer sees only their state data. Mentor sees only their assigned lab data — Lab Type field is locked.',
  },

  'dashboard stats': {
    title: 'Dashboard Stats — Reports',
    roles: [1, 2, 3],
    steps: [
      'Go to Reports → Dashboard Stats tab.',
      'Summary cards show: Total Sessions, Total Attendees, Active Labs, Active Mentors, Schools.',
      'A Sessions per Month bar chart is shown below.',
      'Tables below the chart: Sessions by Lab, Sessions by Mentor, Sessions by School.',
      '— Admin: Filter by From Date, To Date, and State. Click Apply Filter.',
      '— State Officer: Filter by date only (state is auto-applied).',
      '— Mentor: Filter by date only (lab is auto-applied).',
    ],
    note: 'Admin sees platform-wide data (268 sessions, 427 attendees, 71 labs, 17 mentors, 80 schools). State Officer and Mentor see scoped data only.',
  },

  'excel reports': {
    title: 'Download Reports — All Excel Report Types',
    roles: [1, 2, 3],
    steps: [
      'Go to Reports → Download Reports tab.',
      'Select a report type from the categories below, set date filters, and click Download Excel Report.',
      '',
      '📂 LAB TYPE REPORTS (3)',
      '  • Lab Type Sessions Report — All sessions for a specific lab type (Atal/SATHEE/AI etc.)',
      '  • All Lab Types — Combined — All sessions from every lab type in one sheet.',
      '  • All Lab Types — Multi-Sheet — One Excel sheet per lab type with summary.',
      '',
      '📂 MENTOR REPORTS (2)',
      '  • Mentor-wise Sessions Summary — Total sessions per mentor with date range.',
      '  • Mentor Sessions Full Detail — All sessions by a specific mentor.',
      '',
      '📂 SCHOOL REPORTS (2)',
      '  • School-wise Sessions Summary — Total sessions per school with date range.',
      '  • School Sessions Full Detail — All sessions under a specific school.',
      '',
      '📂 SESSIONS (6)',
      '  • All Sessions with School Details',
      '  • Student Attendance (Specific Session)',
      '  • Students Attended per Session',
      '  • Sessions by State',
      '  • Sessions by Host',
      '  • Students with Low Attendance',
      '',
      '📂 SCHOOLS (4)',
      '  • Schools – Total Sessions Conducted',
      '  • Schools with No Sessions',
      '  • Schools Grouped by State',
      '  • Schools with Labs but No Equipment',
      '',
      '📂 LABS (3)',
      '  • Labs per School',
      '  • Labs by Type',
      '  • Labs Added in Period',
      '',
      '📂 EQUIPMENT (5 — Admin full; State Officer: 1 only; Mentor: locked to lab)',
      '  • Equipment Available',
      '  • Equipment – Expired Warranties',
      '  • Recently Added Equipment',
      '  • Equipment Inventory Status',
      '  • Equipment Allocations in Period',
      '',
      '📂 STUDENTS (1)',
      '  • Student Data by Aadhaar',
    ],
    note: 'Mentor: Lab Type is locked to their own lab (shown as "Locked" badge). State Officer: data is scoped to their state. Admin: all states, all labs, all filters available.',
  },

  'pdf reports': {
    title: 'PDF Reports — Download Guide',
    roles: [1, 2, 3],
    steps: [
      'Go to Reports → PDF Reports tab.',
      'PDF reports include session images uploaded during session creation.',
      'Format: Landscape A4 with SLATE branding.',
      'Set From Date and To Date filters.',
      'Optionally select Lab Type.',
      '— Admin only: also filter by State and Lab Type.',
      '',
      'Available PDF Report Types:',
      '  🟣 Sessions with Photos — All sessions with embedded session images (main PDF feature).',
      '  🔵 Sessions Overview — Summary stats: totals, by month, by state.',
      '  🟦 Lab Type Summary — Sessions grouped by lab type (Atal/SATHEE/AI etc.).',
      '  🟠 Mentor Sessions — Sessions grouped by mentor with totals.',
      '  🟢 School Sessions — Sessions grouped by school with totals.',
      '',
      'Click the Download PDF button under the desired report card.',
    ],
    note: 'Mentor: PDF reports restricted to their assigned lab only. State Officer: PDF reports show only their state data. Admin: full access — all states, all labs.',
  },

  'grievance': {
    title: 'How to Submit a Grievance',
    roles: [1, 2, 3],
    steps: [
      'Click "Grievance" in the left sidebar.',
      'Click the "Submit Grievance" tab.',
      '— Issue Details: Select Issue Category from dropdown.',
      'Select Issue Sub-Category (depends on the category selected).',
      'Select Action Required (e.g. Repair/Replacement, Training Required).',
      'Set Priority Level: Low, Medium, or High (default is Medium).',
      '— Location Details (optional): Enter School Name, Lab Name, Equipment Name.',
      '— Description: Write a description of the issue (up to 1000 characters).',
      '— Upload Image: Attach a PNG or JPG image (max 5MB) if available.',
      'Click Submit Grievance button at the bottom right.',
    ],
    note: 'After submission, a unique Grievance ID is assigned (format: SLT-GR-2026-XXXXX). Track its status under the My Grievances tab.',
  },

  'my grievances': {
    title: 'How to View My Grievances',
    roles: [1, 2, 3],
    steps: [
      'Click "Grievance" in the sidebar.',
      'Click the "My Grievances" tab.',
      'You will see all grievances you submitted.',
      'Each row shows: Grievance ID (SLT-GR-2026-XXXXX), Status, Category, and Description.',
      'Status flow: Pending → In Progress → Resolved → Closed.',
    ],
    note: 'Admins can view and manage ALL grievances via God Mode → Manage Grievances. Mentors and State Officers can only view their own submitted grievances here.',
  },

  'profile': {
    title: 'How to Update My Profile',
    roles: [1, 2, 3],
    steps: [
      'Click "My Profile" in the sidebar.',
      'View your avatar (with initials), Name, Email, Role badge, Scope, and Phone.',
      '— Admin scope shows "All India".',
      'To change password: scroll to Reset Password section.',
      'Enter Old Password, New Password, and Confirm Password.',
      'Click Update Password button.',
    ],
    note: 'Email cannot be changed from the profile page. Contact your Administrator if your email needs to be updated.',
  },

  'god mode': {
    title: 'God Mode — Admin-Only Tools',
    roles: [1],
    steps: [
      'God Mode is visible only to Administrators (shown in yellow in the sidebar).',
      '⚙️ System Settings — Set file upload limit (currently 2500 KB), total storage (currently 50 GB), and toggle allowed document formats.',
      '👁️ Check User Activity — Select a user and period (Last 30/90/180/365 days), click Track to see activity logs.',
      '👤 User Access — View all users. Create User, Change Role, Suspend, Edit Details, and Delete User.',
      '🔒 RBAC — Toggle sidebar feature access per user: Add School, Tag Lab, Add Equipment, Equipment Allocation, Session Setup, Reports.',
      '🧪 Add Lab Type — Add new lab type name (max 150 chars). View/Edit/Delete existing lab types.',
      '📄 Define Document Type — Add new document categories for the Library.',
      '🗂️ Manage All Artifacts — View all uploaded documents with author, date, and edit action.',
      '🏷️ Edit Tags — View and edit tags. Only editing is allowed, no deletion.',
      '🏫 Manage Schools & Labs — Filter by name, state, status. View school cards with UDISE, location, contact.',
      '📢 Manage Grievances — Full grievance management with status updates and resolution notes.',
    ],
    note: 'God Mode is ONLY for Administrators. Mentors and State Officers have no access to any God Mode feature.',
  },

  'manage grievances': {
    title: 'How to Manage Grievances (Admin — God Mode)',
    roles: [1],
    steps: [
      'Go to God Mode → Manage Grievances in the sidebar.',
      'The page shows Grievance Management with a Refresh button at top right.',
      'Summary cards at the top show: Total Grievances (12), Pending (7), In Progress (2), Resolved (2), Critical (1).',
      'The table below shows all grievances with columns: Grievance ID, User (name + email), Category, Action Required, Priority badge, Status badge, and Date.',
      '— Filter by Status: All Status / Pending / In Progress / Resolved / Closed.',
      '— Filter by Priority: All Priority / Low / Medium / High / Critical.',
      '— Search by Grievance ID, user email, or category using the search box.',
      'Click any grievance row to open the Grievance Details modal.',
      '— Modal shows: Grievance ID, Submitted By, Email, Issue Category, Sub-Category, Action Required, Priority, School, Lab, Equipment, Submitted On, Description, and Attached Image.',
      'Scroll down in the modal to find the Update Status section.',
      'Select new Status from dropdown: Pending / In Progress / Resolved / Closed.',
      'Add Remarks / Resolution Notes in the textarea (e.g. "Action taken, equipment replaced").',
      'Click the green Update Status button to save changes.',
      'When status is set to Resolved: a red Delete button appears at bottom left of the modal.',
      'Click Delete to permanently remove the grievance if needed.',
    ],
    note: 'Only Administrators can manage all grievances. Priority levels are: Low, Medium, High, Critical. The Delete button only appears when status is Resolved.',
  },

  'add user': {
    title: 'How to Add a New User',
    roles: [1],
    steps: [
      'Go to God Mode → User Access.',
      'Click the Create User button (top right).',
      'A modal opens — fill: First Name, Last Name, Email, Phone.',
      'Select User Type: Admin, Mentor, or State Officer.',
      'Select the State for the user.',
      'Select Lab Type to Assign (4 lab types available).',
      'Click Submit to create the account.',
    ],
    note: 'Only Administrators can create new user accounts. The User Access table also allows: Change User Role, Suspend User (toggle), Edit Details, and Delete User.',
  },

  'rbac': {
    title: 'RBAC — Role Based Access Control',
    roles: [1],
    steps: [
      'Go to God Mode → RBAC.',
      'The table shows all users with their Username, Title (role shown in color).',
      'Toggle columns control access to: Add School, Tag Lab to School, Add Equipment, Equipment Allocation, Session Setup, and Reports.',
      'Toggle ON to grant access, toggle OFF to revoke.',
      'Changes take effect on the user\'s next login or page refresh.',
    ],
    note: 'RBAC gives fine-grained control — e.g. you can give a Mentor access to Reports but block Equipment Allocation. Only Admins can manage RBAC.',
  },

  'system settings': {
    title: 'System Settings (God Mode)',
    roles: [1],
    steps: [
      'Go to God Mode → System Settings.',
      'System Variables table shows: File Upload Limit (currently 2500 KB) with Update button.',
      'Total Space Allocated (currently 50 GB) with Update button.',
      'Document Formats For Library section: toggle ON/OFF for each file type.',
      'Supported toggles: jpg, png, jpeg, doc, pdf, docx, xls, xlsx, zip, csv, ppt, pptx, txt, tar.',
      'Click Update next to any variable to save changes.',
    ],
    note: 'Changes to upload limit and allowed formats affect all users immediately.',
  },

  'check user activity': {
    title: 'Check User Activity (God Mode)',
    roles: [1],
    steps: [
      'Go to God Mode → Check User Activity.',
      'Select a user from the Users dropdown.',
      'Select a Period: Last 30 days, Last 90 days, Last 180 days, or Last 365 days.',
      'Click the Track button.',
      'A log table appears showing: Log ID, Activity description, and Log Date.',
    ],
    note: 'Useful for auditing user actions and monitoring platform engagement.',
  },

  'getting started': {
    title: 'Getting Started with SLATE',
    roles: [1, 2, 3],
    steps: [
      'Step 1 — Add Schools: School → Add School (all roles).',
      'Step 2 — Tag Labs to Schools: School → Tag Lab to School (all roles).',
      'Step 3 — Add Equipment: School → Add Equipment (all roles).',
      'Step 4 — Allocate Equipment to Labs: School → Equipment Allocation (all roles).',
      'Step 5 — Create Sessions: School → Session Setup (all roles).',
      'Step 6 — Register SATHEE Students: School → Sathee Registration (all roles).',
      'Step 7 — Submit Grievances if issues arise: Grievance in sidebar (all roles).',
      'Step 8 — Generate Reports: Reports in sidebar (all roles — scoped by role).',
      'Step 9 — Admin only: Manage users, RBAC, and system settings via God Mode.',
    ],
    note: 'Follow this order for correct setup. Labs must exist before Sessions. Equipment must be added before Allocation.',
  },
};

/* ══════════════════════════════════════════════════════════
   AI-STYLE RESPONSES (greetings + general questions)
══════════════════════════════════════════════════════════ */
const AI_RESPONSES = [
  {
    keys: ['hello', 'hi', 'hey', 'hii', 'helo', 'hlo', 'hai'],
    reply: `Hello! 👋 Welcome to SLATE AI Help Assistant.\n\nI can guide you on sessions, schools, equipment, reports, grievances, library, and more.\n\nJust type your question or pick a topic from the quick buttons!`,
  },
  {
    keys: ['how are you', 'how r u', 'how are u', 'whats up', "what's up", 'wassup'],
    reply: `I'm doing great, thank you for asking! 😊\n\nI'm here and ready to help you with SLATE. What do you need help with today?`,
  },
  {
    keys: ['thank', 'thanks', 'thankyou', 'thank you', 'ty', 'thnx', 'shukriya'],
    reply: `You're welcome! 😊 Feel free to ask anytime you need help with SLATE.`,
  },
  {
    keys: ['bye', 'goodbye', 'good bye', 'see you', 'cya', 'alvida'],
    reply: `Goodbye! 👋 Have a great day. Come back anytime you need SLATE help!`,
  },
  {
    keys: ['good morning', 'good afternoon', 'good evening', 'good night'],
    reply: `Good day! ☀️ Hope you're doing well. How can I assist you with SLATE today?`,
  },
  {
    keys: ['what is slate', 'about slate', 'tell me about slate', 'slate kya hai', 'slate kya h'],
    reply: `**SLATE** stands for **School Lab and Training Environment**.\n\nIt is a web platform for managing:\n- Atal Tinkering Labs\n- SATHEE Kendras\n- AI Labs & Aghaaz Labs\n\nIt allows Admins, Mentors, and State Officers to manage schools, sessions, equipment, students, grievances, and generate detailed reports — all in one place.`,
  },
  {
    keys: ['what can you do', 'help me', 'what do you know', 'capabilities', 'kya kar sakte ho'],
    reply: `I can guide you step-by-step on:\n\n- 🏫 Add School / Tag Lab to School\n- 📅 Create Sessions / View Entries\n- 📦 Add Equipment / Equipment Allocation\n- 📝 Sathee Registration\n- 📚 Library (Upload / Search / Add URL)\n- 📊 Excel Reports / PDF Reports / Dashboard Stats\n- 📢 Submit & View Grievances\n- 🔑 God Mode (Admin only)\n- 👤 Add Users / RBAC / System Settings\n- 🚀 Getting Started guide\n\nJust type any topic!`,
  },
  {
    keys: ['forgot password', 'reset password', "can't login", 'cant login', 'login problem', 'password bhul gaya'],
    reply: `If you forgot your password:\n\n1. Contact your **Administrator** to reset your account.\n2. Once logged in, go to **My Profile** in the sidebar.\n3. Scroll to the **Reset Password** section.\n4. Enter Old Password, New Password, and Confirm Password.\n5. Click **Update Password**.\n\nNote: Email cannot be changed from the profile page — contact Admin for that.`,
  },
  {
    keys: ['who made', 'who created', 'who built', 'developer', 'developed by'],
    reply: `SLATE is a platform built for managing government lab programs (Atal Tinkering Labs, SATHEE, AI Labs) across India.\n\nFor technical support, contact your system administrator.`,
  },
  {
    keys: ['session timeout', 'token expired', 'logged out', 'auto logout', 'session expire'],
    reply: `If you were automatically logged out:\n\n1. Go to **localhost:3000** (or your production URL).\n2. Log in again with your credentials.\n3. Your data is safe — JWT tokens expire after inactivity for security.\n\nThis is normal behavior, not a bug.`,
  },
];

const findAIResponse = (query) => {
  const q = query.toLowerCase().trim();
  for (const { keys, reply } of AI_RESPONSES) {
    if (keys.some(k => q.includes(k))) return reply;
  }
  return null;
};

/* ══════════════════════════════════════════════════════════
   KEYWORD → KNOWLEDGE BASE MATCHER
══════════════════════════════════════════════════════════ */
const KEYWORD_MAP = [
  { keys: ['add school', 'create school', 'new school', 'school add', 'school banana', 'school kaise'], key: 'add school' },
  { keys: ['tag lab', 'lab tag', 'attach lab', 'link lab', 'add lab', 'lab school'], key: 'tag lab to school' },
  { keys: ['add equipment', 'new equipment', 'equipment add', 'equipment banana'], key: 'add equipment' },
  { keys: ['allocate equipment', 'equipment allocation', 'assign equipment', 'equipment assign'], key: 'equipment allocation' },
  { keys: ['session setup', 'create session', 'add session', 'new session', 'session create', 'session kaise', 'setup session', 'session banana'], key: 'session' },
  { keys: ['my entries', 'all entries', 'view session', 'see session', 'session list', 'session dekho', 'download image', 'session image', 'zip download', 'image download', 'sathee students', 'all schools', 'all labs', 'allocated equipment'], key: 'my entries' },
  { keys: ['sathee', 'register student', 'student registration', 'sathee registration'], key: 'sathee registration' },
  { keys: ['submit grievance', 'file grievance', 'add grievance', 'complaint', 'report issue', 'raise grievance', 'new grievance', 'shikayat'], key: 'grievance' },
  { keys: ['my grievance', 'view grievance', 'grievance status', 'check grievance', 'grievance list'], key: 'my grievances' },
  { keys: ['manage grievance', 'grievance manage', 'all grievance', 'admin grievance', 'god mode grievance'], key: 'manage grievances' },
  { keys: ['library', 'upload document', 'document upload', 'add url', 'artifact', 'upload file'], key: 'library' },
  { keys: ['pdf report', 'pdf download', 'download pdf'], key: 'pdf reports' },
  { keys: ['excel report', 'download report', 'excel', 'download excel'], key: 'excel reports' },
  { keys: ['dashboard stat', 'report stat', 'analytics', 'report chart', 'sessions chart'], key: 'dashboard stats' },
  { keys: ['report', 'generate report', 'reports kaise'], key: 'reports' },
  { keys: ['god mode', 'admin tools', 'god mod'], key: 'god mode' },
  { keys: ['add user', 'create user', 'new user', 'invite user', 'register user'], key: 'add user' },
  { keys: ['rbac', 'role based', 'feature access', 'toggle access', 'access control'], key: 'rbac' },
  { keys: ['system settings', 'upload limit', 'storage', 'file limit', 'document format'], key: 'system settings' },
  { keys: ['user activity', 'check activity', 'activity log', 'track user'], key: 'check user activity' },
  { keys: ['profile', 'change password', 'update profile', 'my profile', 'password change'], key: 'profile' },
  { keys: ['getting started', 'start', 'begin', 'how to use', 'workflow', 'process', 'overview', 'first step', 'shuru'], key: 'getting started' },
];

const findAnswer = (query) => {
  const q = query.toLowerCase().trim();
  for (const { keys, key } of KEYWORD_MAP) {
    if (keys.some(k => q.includes(k))) return { key, data: KNOWLEDGE[key] };
  }
  return null;
};

/* ══════════════════════════════════════════════════════════
   QUICK SUGGESTION TOPICS PER ROLE
   ⚠️ VALUES MUST MATCH KEYWORD_MAP KEYS (lowercase)
══════════════════════════════════════════════════════════ */
const SUGGESTIONS = {
  1: [
    { label: 'Getting started',        query: 'getting started' },
    { label: 'Add a school',           query: 'add school' },
    { label: 'Tag lab to school',      query: 'tag lab' },
    { label: 'Create a session',       query: 'create session' },
    { label: 'Add equipment',          query: 'add equipment' },
    { label: 'Equipment allocation',   query: 'allocate equipment' },
    { label: 'Sathee registration',    query: 'sathee registration' },
    { label: 'My entries & sessions',  query: 'my entries' },
    { label: 'Library',                query: 'library' },
    { label: 'Excel reports',          query: 'excel report' },
    { label: 'PDF reports',            query: 'pdf report' },
    { label: 'Dashboard stats',        query: 'dashboard stat' },
    { label: 'Submit grievance',       query: 'submit grievance' },
    { label: 'Manage grievances',      query: 'manage grievance' },
    { label: 'Add user',               query: 'add user' },
    { label: 'RBAC',                   query: 'rbac' },
    { label: 'System settings',        query: 'system settings' },
    { label: 'God mode',               query: 'god mode' },
  ],
  2: [
    { label: 'Getting started',        query: 'getting started' },
    { label: 'Add a school',           query: 'add school' },
    { label: 'Tag lab to school',      query: 'tag lab' },
    { label: 'Create a session',       query: 'create session' },
    { label: 'Add equipment',          query: 'add equipment' },
    { label: 'Equipment allocation',   query: 'allocate equipment' },
    { label: 'Sathee registration',    query: 'sathee registration' },
    { label: 'My entries & sessions',  query: 'my entries' },
    { label: 'Library',                query: 'library' },
    { label: 'Excel reports',          query: 'excel report' },
    { label: 'PDF reports',            query: 'pdf report' },
    { label: 'Submit grievance',       query: 'submit grievance' },
    { label: 'My profile',             query: 'my profile' },
  ],
  3: [
    { label: 'Getting started',        query: 'getting started' },
    { label: 'Add a school',           query: 'add school' },
    { label: 'Tag lab to school',      query: 'tag lab' },
    { label: 'Create a session',       query: 'create session' },
    { label: 'Add equipment',          query: 'add equipment' },
    { label: 'Equipment allocation',   query: 'allocate equipment' },
    { label: 'Sathee registration',    query: 'sathee registration' },
    { label: 'My entries & sessions',  query: 'my entries' },
    { label: 'Library',                query: 'library' },
    { label: 'Excel reports',          query: 'excel report' },
    { label: 'PDF reports',            query: 'pdf report' },
    { label: 'Dashboard stats',        query: 'dashboard stat' },
    { label: 'Submit grievance',       query: 'submit grievance' },
    { label: 'My profile',             query: 'my profile' },
  ],
};

const ROLE_LABELS = { 1: 'Administrator', 2: 'Mentor', 3: 'State Officer' };

/* ══════════════════════════════════════════════════════════
   ROBOT SVG ICON
══════════════════════════════════════════════════════════ */
const RobotIcon = ({ size = 28, color = '#fff' }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <line x1="16" y1="2" x2="16" y2="7" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
    <circle cx="16" cy="2" r="1.5" fill={color} />
    <rect x="6" y="7" width="20" height="14" rx="4" fill={color} fillOpacity="0.15" stroke={color} strokeWidth="1.6" />
    <circle cx="11.5" cy="13" r="2.2" fill={color} />
    <circle cx="20.5" cy="13" r="2.2" fill={color} />
    <circle cx="12.2" cy="12.3" r="0.7" fill="rgba(30,58,138,0.7)" />
    <circle cx="21.2" cy="12.3" r="0.7" fill="rgba(30,58,138,0.7)" />
    <path d="M11 17.5 Q16 20.5 21 17.5" stroke={color} strokeWidth="1.5" strokeLinecap="round" fill="none" />
    <rect x="3" y="10" width="3" height="5" rx="1.5" fill={color} fillOpacity="0.7" />
    <rect x="26" y="10" width="3" height="5" rx="1.5" fill={color} fillOpacity="0.7" />
    <rect x="11" y="21" width="10" height="5" rx="2" fill={color} fillOpacity="0.25" stroke={color} strokeWidth="1.2" />
    <line x1="16" y1="21" x2="16" y2="26" stroke={color} strokeWidth="1.2" />
  </svg>
);

/* ══════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════ */
const SLATEChatbot = () => {
  const [open,      setOpen]      = useState(false);
  const [messages,  setMessages]  = useState([]);
  const [input,     setInput]     = useState('');
  const [roleId,    setRoleId]    = useState(null);
  const [initiated, setInitiated] = useState(false);
  const [btnHover,  setBtnHover]  = useState(false); // ← FIX 1: hover state for tooltip
  const bottomRef = useRef(null);
  const inputRef  = useRef(null);
  const msgLenRef = useRef(0);

  useEffect(() => {
    if (messages.length !== msgLenRef.current) {
      msgLenRef.current = messages.length;
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    if (open && roleId) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open, roleId]);

  useEffect(() => {
    if (open && !initiated) {
      setInitiated(true);
      setMessages([{ type: 'role-select', time: new Date() }]);
    }
  }, [open, initiated]);

  const selectRole = (id) => {
    setRoleId(id);
    setMessages(prev => [...prev,
      { type: 'user', content: ROLE_LABELS[id], time: new Date() },
      {
        type: 'assistant',
        content: `Got it! You are logged in as **${ROLE_LABELS[id]}**.\n\nI will guide you based on your role and access level. Select a topic below or type your question.\n\nFor example:\n- "How to create a session"\n- "How to download PDF reports"\n- "How to submit a grievance"`,
        time: new Date(),
        showSuggestions: true,
        roleId: id,
      },
    ]);
  };

  /* ── send message ───────────────────────────────────── */
  const sendMessage = (text) => {
    const query = (text || input).trim();
    if (!query) return;
    setInput('');

    const aiReply = findAIResponse(query);
    if (aiReply) {
      setMessages(prev => [...prev,
        { type: 'user', content: query, time: new Date() },
        { type: 'assistant', content: aiReply, time: new Date() },
      ]);
      return;
    }

    const found = findAnswer(query);
    let botMsg;

    if (found) {
      const { data } = found;
      const hasAccess = !data.roles || data.roles.includes(roleId);
      if (!hasAccess) {
        botMsg = {
          type: 'assistant',
          content: `🔒 **Access Restricted**\n\nThe feature "${data.title}" is not available for your role (${ROLE_LABELS[roleId]}).\n\nPlease contact your Administrator if you need access to this feature.`,
          time: new Date(),
        };
      } else {
        botMsg = { type: 'guide', data, time: new Date() };
      }
    } else {
      botMsg = {
        type: 'assistant',
        content: `I couldn't find a guide for that.\n\nHere are things I can help with:\n\n- Add School / Tag Lab / Add Equipment\n- Equipment Allocation / Session Setup\n- Sathee Registration / My Entries\n- Library (Upload / Search / Add URL)\n- Excel Reports / PDF Reports\n- Submit Grievance / Manage Grievances\n- God Mode (Admin only)\n- Getting Started\n\nTry typing one of the above!`,
        time: new Date(),
      };
    }

    setMessages(prev => [...prev,
      { type: 'user', content: query, time: new Date() },
      botMsg,
    ]);
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const clearChat = () => {
    setMessages([]);
    setRoleId(null);
    setInitiated(false);
    msgLenRef.current = 0;
  };

  const fmtTime = d => d?.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });

  /* ── sub-components ─────────────────────────────────── */
  const Avatar = () => (
    <div style={{
      width: 26, height: 26, borderRadius: 7, flexShrink: 0, marginTop: 2,
      background: 'linear-gradient(135deg,#1e3a8a,#2563eb)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <RobotIcon size={17} color="#fff" />
    </div>
  );

  const Bubble = ({ children }) => (
    <div style={{
      background: '#fff', color: '#1e293b',
      borderRadius: '12px 12px 12px 3px',
      padding: '11px 13px', lineHeight: 1.65,
      boxShadow: '0 1px 6px rgba(0,0,0,.08)',
      border: '1px solid #e2e8f0',
    }}>{children}</div>
  );

  const MsgTime = ({ children }) => (
    <p style={{ margin: '3px 0 0', fontSize: 9.5, color: '#94a3b8' }}>{children}</p>
  );

  const renderText = (content) =>
    content.split('\n').map((line, li) => {
      const html = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      if (line.startsWith('- ')) return (
        <div key={li} style={{ display: 'flex', gap: 7, marginBottom: 3 }}>
          <span style={{ color: '#2563eb', fontWeight: 700, flexShrink: 0 }}>—</span>
          <span style={{ fontSize: 12.5, color: '#1e293b', lineHeight: 1.65 }}
            dangerouslySetInnerHTML={{ __html: html.slice(2) }} />
        </div>
      );
      if (line.trim() === '') return <div key={li} style={{ height: 4 }} />;
      return (
        <div key={li} style={{ fontSize: 12.5, color: '#1e293b', lineHeight: 1.65 }}
          dangerouslySetInnerHTML={{ __html: html }} />
      );
    });

  /* ── render each message ────────────────────────────── */
  const renderMsg = (msg, i) => {

    if (msg.type === 'role-select') return (
      <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'flex-start' }}>
        <Avatar />
        <div style={{ flex: 1 }}>
          <Bubble>
            <p style={{ margin: '0 0 12px', fontSize: 12.5, color: '#1e293b', lineHeight: 1.6 }}>
              Welcome to <strong>SLATE AI Help Assistant</strong> 👋<br />
              Please select your role so I can give you accurate guidance:
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              {[
                { id: 1, label: 'Administrator', desc: 'Full access — all features, God Mode, 26 reports', icon: 'bx-shield' },
                { id: 2, label: 'Mentor',        desc: 'Lab-level access — your assigned lab, 11 reports', icon: 'bx-user' },
                { id: 3, label: 'State Officer', desc: 'State-level access — your state only, 22 reports', icon: 'bx-map' },
              ].map(r => (
                <button key={r.id} onClick={() => selectRole(r.id)} style={{
                  display: 'flex', alignItems: 'center', gap: 11,
                  background: '#f8fafc', border: '1.5px solid #e2e8f0',
                  borderRadius: 10, padding: '9px 13px',
                  cursor: 'pointer', transition: 'all .18s', textAlign: 'left',
                }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = '#1e3a8a';
                    e.currentTarget.style.borderColor = '#1e3a8a';
                    e.currentTarget.querySelector('.rl').style.color = '#fff';
                    e.currentTarget.querySelector('.rd').style.color = 'rgba(255,255,255,.75)';
                    e.currentTarget.querySelector('.ri').style.color = '#fff';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = '#f8fafc';
                    e.currentTarget.style.borderColor = '#e2e8f0';
                    e.currentTarget.querySelector('.rl').style.color = '#1e293b';
                    e.currentTarget.querySelector('.rd').style.color = '#64748b';
                    e.currentTarget.querySelector('.ri').style.color = '#1e3a8a';
                  }}>
                  <i className={`bx ${r.icon} ri`} style={{ fontSize: 18, color: '#1e3a8a', flexShrink: 0 }} />
                  <div>
                    <p className="rl" style={{ margin: 0, fontSize: 12.5, fontWeight: 700, color: '#1e293b' }}>{r.label}</p>
                    <p className="rd" style={{ margin: 0, fontSize: 11, color: '#64748b' }}>{r.desc}</p>
                  </div>
                </button>
              ))}
            </div>
          </Bubble>
          <MsgTime>{fmtTime(msg.time)}</MsgTime>
        </div>
      </div>
    );

    if (msg.type === 'user') return (
      <div key={i} style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <div>
          <div style={{
            background: '#1e3a8a', color: '#fff',
            borderRadius: '12px 12px 3px 12px',
            padding: '9px 13px', fontSize: 12.5, lineHeight: 1.6,
            boxShadow: '0 2px 8px rgba(30,58,138,.2)', maxWidth: 280,
          }}>{msg.content}</div>
          <p style={{ margin: '3px 0 0', fontSize: 9.5, color: '#94a3b8', textAlign: 'right' }}>{fmtTime(msg.time)}</p>
        </div>
      </div>
    );

    if (msg.type === 'assistant') return (
      <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'flex-start' }}>
        <Avatar />
        <div style={{ maxWidth: '88%' }}>
          <Bubble>
            {renderText(msg.content)}
            {msg.showSuggestions && msg.roleId && (
              <div style={{ marginTop: 12 }}>
                <p style={{ margin: '0 0 7px', fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: .7 }}>
                  Quick Help Topics
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                  {/* ← FIX 2: use .query for sendMessage, .label for display */}
                  {(SUGGESTIONS[msg.roleId] || []).map((s, si) => (
                    <button key={si} onClick={() => sendMessage(s.query)} style={{
                      background: '#eff6ff', border: '1px solid #bfdbfe',
                      borderRadius: 6, padding: '4px 10px',
                      fontSize: 11, color: '#1e3a8a', cursor: 'pointer',
                      fontWeight: 500, transition: 'all .15s',
                    }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#1e3a8a'; e.currentTarget.style.color = '#fff'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.color = '#1e3a8a'; }}>
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </Bubble>
          <MsgTime>{fmtTime(msg.time)}</MsgTime>
        </div>
      </div>
    );

    if (msg.type === 'guide') {
      const { data } = msg;
      return (
        <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'flex-start' }}>
          <Avatar />
          <div style={{ flex: 1 }}>
            <div style={{
              background: '#fff', borderRadius: '12px 12px 12px 3px',
              boxShadow: '0 2px 10px rgba(0,0,0,.08)',
              border: '1px solid #e2e8f0', overflow: 'hidden',
            }}>
              <div style={{ background: 'linear-gradient(135deg,#1e3a8a,#1d4ed8)', padding: '10px 14px' }}>
                <p style={{ margin: 0, fontSize: 12.5, fontWeight: 700, color: '#fff', letterSpacing: .2 }}>
                  📖 {data.title}
                </p>
              </div>
              <div style={{ padding: '12px 14px' }}>
                <p style={{ margin: '0 0 9px', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: .7 }}>
                  Step-by-step Guide
                </p>
                {(() => {
                  let stepNum = 0;
                  return data.steps.map((step, si) => {
                    if (step === '') return <div key={si} style={{ height: 6 }} />;
                    if (
                      step.startsWith('📂') ||
                      step.startsWith('  •') ||
                      step.startsWith('  🟣') ||
                      step.startsWith('  🔵') ||
                      step.startsWith('  🟦') ||
                      step.startsWith('  🟠') ||
                      step.startsWith('  🟢')
                    ) {
                      return (
                        <div key={si} style={{
                          fontSize: step.startsWith('  ') ? 11.5 : 12,
                          color: step.startsWith('  ') ? '#475569' : '#1e3a8a',
                          fontWeight: step.startsWith('  ') ? 400 : 700,
                          marginBottom: 4,
                          paddingLeft: step.startsWith('  ') ? 14 : 0,
                          lineHeight: 1.6,
                        }}>{step}</div>
                      );
                    }
                    if (
                      step.startsWith('— ') ||
                      step.startsWith('⚙️') || step.startsWith('👁️') ||
                      step.startsWith('👤') || step.startsWith('🔒') ||
                      step.startsWith('🧪') || step.startsWith('📄') ||
                      step.startsWith('🗂️') || step.startsWith('🏷️') ||
                      step.startsWith('🏫') || step.startsWith('📢')
                    ) {
                      return (
                        <div key={si} style={{
                          fontSize: 12, color: '#1e40af', fontWeight: 600,
                          marginBottom: 5, lineHeight: 1.6,
                        }}>{step}</div>
                      );
                    }
                    stepNum++;
                    return (
                      <div key={si} style={{ display: 'flex', gap: 10, marginBottom: 7, alignItems: 'flex-start' }}>
                        <div style={{
                          width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                          background: '#eff6ff', border: '1.5px solid #bfdbfe',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 10, fontWeight: 700, color: '#1e3a8a',
                        }}>{stepNum}</div>
                        <p style={{ margin: 0, fontSize: 12, color: '#334155', lineHeight: 1.6 }}>{step}</p>
                      </div>
                    );
                  });
                })()}
                {data.note && (
                  <div style={{
                    marginTop: 10, padding: '8px 11px',
                    background: '#f0f9ff', borderRadius: 8,
                    borderLeft: '3px solid #2563eb',
                  }}>
                    <p style={{ margin: 0, fontSize: 11, color: '#1e40af', lineHeight: 1.5 }}>
                      <strong>💡 Note:</strong> {data.note}
                    </p>
                  </div>
                )}
                <p style={{ margin: '12px 0 0', fontSize: 11, color: '#94a3b8' }}>
                  Need help with something else? Type your question below.
                </p>
              </div>
            </div>
            <MsgTime>{fmtTime(msg.time)}</MsgTime>
          </div>
        </div>
      );
    }

    return null;
  };

  /* ══════════════════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════════════════ */
  return (
    <>
      {/* ── TOOLTIP — only on hover (FIX 1) ── */}
      {!open && btnHover && (
        <div style={{
          position: 'fixed', bottom: 96, right: 28, zIndex: 9000,
          background: '#1e3a8a', color: '#fff',
          fontSize: 10.5, fontWeight: 700, letterSpacing: .4,
          padding: '5px 11px', borderRadius: 20,
          boxShadow: '0 2px 10px rgba(30,58,138,.4)',
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
          animation: 'slateUp .2s ease',
        }}>
          SLATE AI Help Assistant ✨
          <div style={{
            position: 'absolute', bottom: -5, right: 20,
            width: 10, height: 6,
            background: '#1e3a8a',
            clipPath: 'polygon(0 0, 100% 0, 50% 100%)',
          }} />
        </div>
      )}

      {/* ── FLOATING BUTTON ── */}
      <div
        onClick={() => setOpen(o => !o)}
        onMouseEnter={() => setBtnHover(true)}
        onMouseLeave={() => setBtnHover(false)}
        title="SLATE AI Help Assistant"
        style={{
          position: 'fixed', bottom: 28, right: 28, zIndex: 9000,
          width: 58, height: 58, borderRadius: 16,
          background: open ? '#1e293b' : 'linear-gradient(135deg,#1e3a8a,#2563eb)',
          boxShadow: open ? '0 4px 20px rgba(0,0,0,.3)' : '0 6px 28px rgba(37,99,235,.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', transition: 'all .25s',
          border: '2px solid rgba(255,255,255,.18)',
        }}
      >
        {open
          ? <i className="bx bx-x" style={{ fontSize: 26, color: '#fff' }} />
          : <RobotIcon size={30} color="#fff" />
        }
        {!open && (
          <div style={{
            position: 'absolute', top: -6, right: -6,
            background: 'linear-gradient(135deg,#f59e0b,#ef4444)',
            color: '#fff', fontSize: 8.5, fontWeight: 800,
            padding: '2px 5px', borderRadius: 8,
            border: '1.5px solid #fff', letterSpacing: .5,
            boxShadow: '0 2px 6px rgba(0,0,0,.2)',
          }}>AI</div>
        )}
        {!open && (
          <div style={{
            position: 'absolute', bottom: 6, right: 6,
            width: 8, height: 8, borderRadius: '50%',
            background: '#22c55e', border: '2px solid #fff',
          }} />
        )}
      </div>

      {/* ── CHAT PANEL ── */}
      {open && (
        <div style={{
          position: 'fixed', bottom: 98, right: 28, zIndex: 9001,
          width: Math.min(440, window.innerWidth - 40),
          height: Math.min(640, window.innerHeight - 120),
          background: '#fff', borderRadius: 16,
          boxShadow: '0 24px 64px rgba(0,0,0,.16), 0 0 0 1px rgba(0,0,0,.06)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          animation: 'slateUp .2s ease',
        }}>

          {/* HEADER */}
          <div style={{
            background: 'linear-gradient(135deg,#1e3a8a,#1d4ed8)',
            padding: '13px 15px', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 38, height: 38, borderRadius: 10,
                background: 'rgba(255,255,255,.12)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '1px solid rgba(255,255,255,.2)',
              }}>
                <RobotIcon size={24} color="#fff" />
              </div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#fff', letterSpacing: .2 }}>
                    SLATE AI Help Assistant
                  </p>
                  <span style={{
                    background: 'linear-gradient(135deg,#f59e0b,#ef4444)',
                    color: '#fff', fontSize: 8, fontWeight: 800,
                    padding: '1px 5px', borderRadius: 5, letterSpacing: .5,
                  }}>AI</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 2 }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#4ade80' }} />
                  <p style={{ margin: 0, fontSize: 10.5, color: 'rgba(255,255,255,.75)' }}>
                    {roleId ? `${ROLE_LABELS[roleId]} · Platform Guide` : 'Select your role to begin'}
                  </p>
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={clearChat} title="Reset Chat" style={{
                background: 'rgba(255,255,255,.1)', border: '1px solid rgba(255,255,255,.15)',
                borderRadius: 7, width: 30, height: 30, color: '#fff',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,.22)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,.1)'}>
                <i className="bx bx-rotate-left" style={{ fontSize: 14 }} />
              </button>
              <button onClick={() => setOpen(false)} title="Close" style={{
                background: 'rgba(255,255,255,.1)', border: '1px solid rgba(255,255,255,.15)',
                borderRadius: 7, width: 30, height: 30, color: '#fff',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,.5)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,.1)'}>
                <i className="bx bx-x" style={{ fontSize: 16 }} />
              </button>
            </div>
          </div>

          {/* MESSAGES */}
          <div style={{
            flex: 1, overflowY: 'auto', padding: '14px 12px',
            background: '#f8fafc', display: 'flex', flexDirection: 'column',
          }}>
            {messages.map((msg, i) => renderMsg(msg, i))}
            <div ref={bottomRef} />
          </div>

          {/* INPUT BAR */}
          <div style={{
            padding: '10px 12px 12px',
            borderTop: '1px solid #e2e8f0',
            background: '#fff', flexShrink: 0,
          }}>
            <div style={{
              display: 'flex', gap: 8, alignItems: 'center',
              background: roleId ? '#f8fafc' : '#f1f5f9',
              border: '1.5px solid #e2e8f0',
              borderRadius: 10, padding: '7px 10px',
              opacity: roleId ? 1 : 0.65,
            }}>
              <input
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={roleId ? 'Type your question here…' : 'Please select your role above first…'}
                disabled={!roleId}
                style={{
                  flex: 1, border: 'none', background: 'transparent',
                  outline: 'none', fontSize: 12.5, color: '#1e293b',
                  fontFamily: 'inherit', cursor: roleId ? 'text' : 'not-allowed',
                }}
              />
              <button
                onClick={() => sendMessage()}
                disabled={!input.trim() || !roleId}
                style={{
                  width: 32, height: 32, borderRadius: 8, border: 'none',
                  background: input.trim() && roleId ? '#1e3a8a' : '#e2e8f0',
                  color: input.trim() && roleId ? '#fff' : '#94a3b8',
                  cursor: input.trim() && roleId ? 'pointer' : 'default',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all .2s', flexShrink: 0,
                }}>
                <i className="bx bx-send" style={{ fontSize: 14 }} />
              </button>
            </div>
            <p style={{
              margin: '6px 0 0', fontSize: 9.5, color: '#94a3b8',
              textAlign: 'center', letterSpacing: .3,
            }}>
              ✨ Powered by AI · SLATE Platform Guide
            </p>
          </div>

        </div>
      )}

      <style>{`
        @keyframes slateUp {
          from { opacity: 0; transform: translateY(14px) scale(.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </>
  );
};

export default SLATEChatbot;
